<script setup lang="ts">
/**
 * 里程碑挽回计划（0923 Tony 按「项目状态评估准则」拍板）。
 *
 * 准则的交付一条：延迟了、没有计划兜底 = 红；有风险、但有合理方案 = 黄。
 * 里程碑逾期后，项目经理在这里写「挽回到哪天 + 怎么挽回」，项目灯从红落到黄；
 * 挽回日期过了还没完成，灯自己回到红。改 Gate 的计划日期不算挽回——那是改基线。
 *
 * 健康度明细和项目概览的里程碑列表共用这一个。谁能填由后端判（本项目项目经理、管理员、director），
 * 这里 editable=false 时只显示现状、不给表单。
 */
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage, formatDate } from '@/utils/format';
import {
  type RecoverableMilestone, milestoneOverdueDays, recoveryState, shanghaiTodayText,
} from '@/utils/milestone-recovery';

const props = withDefaults(defineProps<{
  projectId: number | string;
  milestone: RecoverableMilestone;
  /** 东八区的今天 YYYY-MM-DD；不传就按东八区现算 */
  today?: string | null;
  editable?: boolean;
}>(), { today: null, editable: false });

const emit = defineEmits<{ saved: [milestone: RecoverableMilestone] }>();

const todayText = computed(() => (props.today || shanghaiTodayText()).slice(0, 10));
const recovery = computed(() => String(props.milestone.recoveryDate ?? '').slice(0, 10));
const overdueDays = computed(() => milestoneOverdueDays(props.milestone, todayText.value));
/** 现在是哪一档：和项目灯的判据同一句话（project-rag.ts 的 milestoneDelayFacts） */
const state = computed(() => recoveryState(props.milestone, todayText.value));

const open = ref(false);
const date = ref(recovery.value && recovery.value >= todayText.value ? recovery.value : '');
const note = ref(String(props.milestone.recoveryNote ?? ''));
const saving = ref(false);
const error = ref('');

async function save(): Promise<void> {
  if (saving.value) return;
  if (!date.value) { error.value = '选一个挽回日期：打算哪天把这个里程碑做完。'; return; }
  if (date.value < todayText.value) { error.value = `挽回日期不能早于今天（${todayText.value}）。`; return; }
  if (!note.value.trim()) { error.value = '要写挽回措施：领导看的是怎么挽回，不是一个新日期。'; return; }
  saving.value = true;
  error.value = '';
  try {
    const saved = await eqsApi.registerMilestoneRecovery(props.projectId, props.milestone.id, {
      recoveryDate: date.value, note: note.value.trim(),
    });
    notify('success', `「${props.milestone.milestoneCode}」已登记挽回计划`, `挽回到 ${date.value}；这个日期前完成，项目灯按黄算。`);
    open.value = false;
    emit('saved', { ...props.milestone, ...saved });
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div v-if="state !== 'none' || open" class="ms-recovery" :class="`is-${state}`">
    <p class="ms-recovery-line">
      <template v-if="state === 'planned'">
        <b>挽回中</b> 逾期 {{ overdueDays }} 天，挽回到 {{ formatDate(milestone.recoveryDate) }}：{{ milestone.recoveryNote }}
        <small>
          {{ milestone.recoveryByName || '项目经理' }}<template v-if="milestone.recoveryAt"> · {{ formatDate(milestone.recoveryAt) }}</template>
          <template v-if="(milestone.recoveryCount ?? 0) > 1"> · 第 {{ milestone.recoveryCount }} 次登记</template>
        </small>
      </template>
      <template v-else-if="state === 'expired'">
        <b>挽回日期也过了</b> 原计划挽回到 {{ formatDate(milestone.recoveryDate) }}，还没完成：{{ milestone.recoveryNote }}
      </template>
      <template v-else-if="state === 'unplanned'">
        <b>逾期 {{ overdueDays }} 天，没有挽回计划</b> 项目灯按红算；登记了挽回计划、日期没过之前按黄算。
      </template>
      <button
        v-if="editable && !open"
        type="button"
        class="inline-button"
        @click="open = true"
      >{{ state === 'planned' ? '改挽回计划' : '登记挽回计划' }}</button>
    </p>
    <div v-if="editable && open" class="ms-recovery-form">
      <label>
        <span>挽回到</span>
        <input v-model="date" type="date" :min="todayText" :aria-label="`${milestone.milestoneCode} 的挽回日期`">
      </label>
      <label class="ms-recovery-note">
        <span>怎么挽回</span>
        <input v-model="note" type="text" placeholder="例：周五补开 Gate2 评审；数据冻结缺的两份图纸周三前客户给" :aria-label="`${milestone.milestoneCode} 的挽回措施`">
      </label>
      <button type="button" class="button button-primary" :disabled="saving" @click="save">{{ saving ? '保存中…' : '保存' }}</button>
      <button type="button" class="button" :disabled="saving" @click="open = false; error = ''">取消</button>
      <p v-if="error" class="ms-recovery-error">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.ms-recovery { display: grid; gap: 6px; padding: 6px 10px; border-left: 3px solid var(--line-strong); background: var(--surface); }
.ms-recovery.is-planned { border-left-color: #f5c33b; background: #fffaeb; }
.ms-recovery.is-expired, .ms-recovery.is-unplanned { border-left-color: var(--risk); background: #fff5f4; }
.ms-recovery-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin: 0; font-size: 12.5px; }
.ms-recovery-line b { font-weight: 800; }
.is-planned .ms-recovery-line b { color: #8a5d00; }
.is-expired .ms-recovery-line b, .is-unplanned .ms-recovery-line b { color: var(--risk); }
.ms-recovery-line small { color: var(--muted); font-size: 11.5px; }
.ms-recovery-form { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ms-recovery-form label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); }
.ms-recovery-note { flex: 1 1 280px; }
.ms-recovery-note input { flex: 1; min-width: 0; }
.ms-recovery-error { flex-basis: 100%; margin: 0; color: var(--red); font-size: 12.5px; font-weight: 700; }
</style>
